import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Arabic namespace imports
import arCommon from './locales/ar/common.json';
import arDashboard from './locales/ar/dashboard.json';
import arShipments from './locales/ar/shipments.json';
import arContracts from './locales/ar/contracts.json';
import arFinance from './locales/ar/finance.json';
import arDocuments from './locales/ar/documents.json';
import arCustoms from './locales/ar/customs.json';
import arOperations from './locales/ar/operations.json';
import arEllecleme from './locales/ar/ellecleme.json';
import arFumigation from './locales/ar/fumigation.json';
import arClearanceRequests from './locales/ar/clearanceRequests.json';
import arWarehouseExpenses from './locales/ar/warehouseExpenses.json';

// English namespace imports
import enCommon from './locales/en/common.json';
import enDashboard from './locales/en/dashboard.json';
import enShipments from './locales/en/shipments.json';
import enContracts from './locales/en/contracts.json';
import enFinance from './locales/en/finance.json';
import enDocuments from './locales/en/documents.json';
import enCustoms from './locales/en/customs.json';
import enOperations from './locales/en/operations.json';
import enEllecleme from './locales/en/ellecleme.json';
import enFumigation from './locales/en/fumigation.json';
import enClearanceRequests from './locales/en/clearanceRequests.json';
import enWarehouseExpenses from './locales/en/warehouseExpenses.json';

// Turkish namespace imports
import trCommon from './locales/tr/common.json';
import trDashboard from './locales/tr/dashboard.json';
import trShipments from './locales/tr/shipments.json';
import trContracts from './locales/tr/contracts.json';
import trFinance from './locales/tr/finance.json';
import trDocuments from './locales/tr/documents.json';
import trCustoms from './locales/tr/customs.json';
import trOperations from './locales/tr/operations.json';
import trEllecleme from './locales/tr/ellecleme.json';
import trFumigation from './locales/tr/fumigation.json';
import trClearanceRequests from './locales/tr/clearanceRequests.json';
import trWarehouseExpenses from './locales/tr/warehouseExpenses.json';

// Merge all namespaces into single translation object per language
// This maintains backward compatibility - all keys work with default namespace.
// The merge is deep: several files define the same top-level section (e.g.
// "products" exists in common, operations, and fumigation), and a shallow
// spread would keep only the last file's section, dropping every other file's
// keys under that section.
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const deepMerge = (
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> => {
  const result = { ...target };
  for (const [key, value] of Object.entries(source)) {
    const existing = result[key];
    result[key] =
      isPlainObject(existing) && isPlainObject(value) ? deepMerge(existing, value) : value;
  }
  return result;
};

const mergeNamespaces = (...namespaces: Record<string, unknown>[]) => {
  return namespaces.reduce((acc, ns) => deepMerge(acc, ns), {});
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: {
        translation: mergeNamespaces(
          arCommon,
          arDashboard,
          arShipments,
          arContracts,
          arFinance,
          arDocuments,
          arCustoms,
          arOperations,
          arEllecleme,
          arFumigation,
          arClearanceRequests,
          arWarehouseExpenses
        ),
      },
      en: {
        translation: mergeNamespaces(
          enCommon,
          enDashboard,
          enShipments,
          enContracts,
          enFinance,
          enDocuments,
          enCustoms,
          enOperations,
          enEllecleme,
          enFumigation,
          enClearanceRequests,
          enWarehouseExpenses
        ),
      },
      tr: {
        translation: mergeNamespaces(
          trCommon,
          trDashboard,
          trShipments,
          trContracts,
          trFinance,
          trDocuments,
          trCustoms,
          trOperations,
          trEllecleme,
          trFumigation,
          trClearanceRequests,
          trWarehouseExpenses
        ),
      },
    },
    // Prefer English when a key is missing so Turkish/English screens never
    // leak Arabic. Arabic users still get Arabic from their complete bundle.
    fallbackLng: 'en',
    // Do NOT set `lng` here — LanguageDetector (localStorage → navigator)
    // must win so the user's last choice and browser locale are respected.
    // First-visit default is still Arabic when neither source yields a match
    // because `supportedLngs` + detector fall through, and Layout applies
    // any locked ui_preferences.language.
    supportedLngs: ['ar', 'en', 'tr'],
    // Normalize regional codes (e.g. "ar-SA" -> "ar") so that
    // `i18n.language === 'ar'` checks throughout the app work for
    // any user whose browser reports a regional Arabic/English/Turkish locale.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    ...(import.meta.env.DEV
      ? {
          saveMissing: true,
          missingKeyHandler: (
            lngs: readonly string[],
            _ns: string,
            key: string,
            _fallback: string
          ) => {
            console.warn(`[i18n] missing key "${key}" for language(s): ${lngs.join(', ')}`);
          },
        }
      : {}),
  });

export default i18n;
